import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface AssetRow {
  readonly assetId: string;
  readonly assetCode: string;
  readonly assetName: string;
  readonly assetType: string;
  readonly category: string;
  readonly classification: string;
  readonly lifecycleStatus: string;
  readonly approvalStatus: string;
}

const STATUSES = ['', 'DRAFT', 'UPLOADED', 'UNDER_REVIEW', 'PENDING_APPROVAL', 'APPROVED', 'PUBLISHED', 'DISTRIBUTED', 'ARCHIVED', 'EXPIRED', 'RETIRED'];
const ASSET_TYPES = ['', 'IMAGE', 'VIDEO', 'AUDIO', 'DOCUMENT', 'VECTOR', 'FONT', 'TEMPLATE', 'ARCHIVE', 'PRESENTATION', 'SPREADSHEET', 'OTHER'];
const CATEGORIES = ['', 'CORPORATE_BRANDING', 'PRODUCT_BRANDING', 'CAMPAIGN_ASSETS', 'PRINT_MEDIA', 'OUTDOOR_MEDIA', 'DIGITAL_MEDIA', 'SOCIAL_MEDIA_CREATIVES', 'VIDEO_ASSETS', 'PHOTOGRAPHY_ASSETS', 'VENDOR_DELIVERABLES', 'TEMPLATES', 'REFERENCE_ASSETS'];
const CLASSIFICATIONS = ['', 'PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];
const ACTIVE_CATEGORIES = ['CORPORATE_BRANDING', 'PRODUCT_BRANDING', 'CAMPAIGN_ASSETS', 'PRINT_MEDIA', 'OUTDOOR_MEDIA', 'DIGITAL_MEDIA', 'SOCIAL_MEDIA_CREATIVES', 'VIDEO_ASSETS', 'PHOTOGRAPHY_ASSETS', 'VENDOR_DELIVERABLES', 'TEMPLATES', 'REFERENCE_ASSETS'];
const TERMINAL = ['ARCHIVED', 'EXPIRED', 'RETIRED'];

/**
 * M36 — Asset Repository (§31). Central register of digital assets (T480)
 * across the 11 asset types and 12 categories, carrying the full lifecycle
 * (DRAFT → PUBLISHED → ARCHIVED) and the current approval status. Filterable
 * by lifecycle status, asset type, category, security classification and a
 * free-text search. Tenant isolation is enforced server-side (LAW 4).
 */
export function AssetRepository(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<AssetRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [assetType, setAssetType] = useState('');
  const [category, setCategory] = useState('');
  const [classification, setClassification] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [versionId, setVersionId] = useState<string | null>(null);
  const [versionNumber, setVersionNumber] = useState('');
  const [catId, setCatId] = useState<string | null>(null);
  const [catValue, setCatValue] = useState(ACTIVE_CATEGORIES[0]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (assetType) params.set('assetType', assetType);
    if (category) params.set('category', category);
    if (classification) params.set('classification', classification);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: AssetRow[]; total: number }>(`/v1/dam/assets${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, assetType, category, classification, q, reload]);

  const run = async (
    r: AssetRow,
    label: string,
    fn: () => Promise<unknown>,
    manageOnFail = t('m36.common.permRequired', { perm: 'M36_ASSET_MANAGE' }),
  ): Promise<void> => {
    setNotice(null);
    setBusyId(r.assetId);
    try {
      await fn();
      setNotice(t('m36.common.codeLabel', { code: r.assetCode, label }));
      setRejectId(null); setRejectReason('');
      setVersionId(null); setVersionNumber('');
      setCatId(null);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setNotice(manageOnFail);
      else setNotice((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const post = (r: AssetRow, action: string, body?: unknown) =>
    apiRequest(`/v1/dam/assets/${r.assetId}/${action}`, { method: 'POST', body, token });

  return (
    <main aria-label="asset-repository">
      <h1>{t('m36.repository.title')}</h1>
      <div className="filters">
        <label>
          {t('m36.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m36.common.type')}
          <select aria-label="filter-type" value={assetType} onChange={(e) => setAssetType(e.target.value)}>
            {ASSET_TYPES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m36.common.category')}
          <select aria-label="filter-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m36.common.classification')}
          <select aria-label="filter-classification" value={classification} onChange={(e) => setClassification(e.target.value)}>
            {CLASSIFICATIONS.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('common.search')}
          <input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m36.repository.searchPh')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status" aria-label="asset-notice">{notice}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="asset-count">{t('m36.repository.count', { count: total })}</p>
          <DataTable<AssetRow>
            ariaLabel="asset-table"
            testId="asset-table"
            rowKey={(r) => r.assetId}
            rowDataAttrs={(r) => ({ 'data-status': r.lifecycleStatus, 'data-approval': r.approvalStatus })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m36.common.code'), render: (r) => r.assetCode },
              { key: 'name', header: t('m36.common.name'), render: (r) => r.assetName },
              { key: 'type', header: t('m36.common.type'), render: (r) => r.assetType },
              { key: 'category', header: t('m36.common.category'), render: (r) => r.category },
              { key: 'classification', header: t('m36.common.classification'), render: (r) => r.classification },
              { key: 'status', header: t('m36.common.status'), render: (r) => r.lifecycleStatus },
              { key: 'approval', header: t('m36.repository.col.approval'), render: (r) => r.approvalStatus },
              { key: 'actions', header: t('m36.common.actions'), render: (r) => {
                const s = r.lifecycleStatus;
                const disabled = busyId === r.assetId;
                return (
                  <>
                    {(s === 'DRAFT' || s === 'UPLOADED') && (
                      <Button variant="secondary" type="button" aria-label={`submit-review-${r.assetCode}`} disabled={disabled} onClick={() => void run(r, t('m36.repository.act.submittedReview'), () => post(r, 'submit-review'))}>{t('m36.repository.submitReview')}</Button>
                    )}
                    {s === 'UNDER_REVIEW' && (
                      <Button variant="secondary" type="button" aria-label={`submit-approval-${r.assetCode}`} disabled={disabled} onClick={() => void run(r, t('m36.repository.act.submittedApproval'), () => post(r, 'submit-approval'))}>{t('m36.repository.submitApproval')}</Button>
                    )}
                    {s === 'PENDING_APPROVAL' && (rejectId === r.assetId ? (
                      <>
                        <input aria-label={`reject-reason-${r.assetCode}`} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={t('m36.common.reasonPh')} />
                        <Button variant="danger" type="button" aria-label={`confirm-reject-${r.assetCode}`} disabled={disabled || !rejectReason.trim()} onClick={() => void run(r, t('m36.repository.act.rejected'), () => post(r, 'reject', { reason: rejectReason.trim() }), t('m36.common.permRequired', { perm: 'M36_ASSET_APPROVE' }))}>{t('m36.common.confirmReject')}</Button>
                        <Button variant="danger" type="button" aria-label={`cancel-reject-${r.assetCode}`} onClick={() => { setRejectId(null); setRejectReason(''); }}>{t('common.cancel')}</Button>
                      </>
                    ) : (
                      <>
                        <Button variant="secondary" type="button" aria-label={`approve-${r.assetCode}`} disabled={disabled} onClick={() => void run(r, t('m36.repository.act.approved'), () => post(r, 'approve'), t('m36.common.permRequired', { perm: 'M36_ASSET_APPROVE' }))}>{t('m36.common.approve')}</Button>
                        <Button variant="danger" type="button" aria-label={`reject-${r.assetCode}`} disabled={disabled} onClick={() => { setRejectId(r.assetId); setRejectReason(''); setNotice(null); }}>{t('m36.common.reject')}</Button>
                      </>
                    ))}
                    {s === 'APPROVED' && (
                      <Button variant="primary" type="button" aria-label={`publish-${r.assetCode}`} disabled={disabled} onClick={() => void run(r, t('m36.repository.act.published'), () => post(r, 'publish'), t('m36.common.permRequired', { perm: 'M36_ASSET_PUBLISH' }))}>{t('m36.common.publish')}</Button>
                    )}
                    {s === 'PUBLISHED' && (
                      <Button variant="secondary" type="button" aria-label={`distribute-${r.assetCode}`} disabled={disabled} onClick={() => void run(r, t('m36.repository.act.distributed'), () => post(r, 'distribute'), t('m36.common.permRequired', { perm: 'M36_DISTRIBUTION_MANAGE' }))}>{t('m36.repository.distribute')}</Button>
                    )}
                    {!TERMINAL.includes(s) && (
                      <Button variant="danger" type="button" aria-label={`archive-${r.assetCode}`} disabled={disabled} onClick={() => void run(r, t('m36.repository.act.archived'), () => post(r, 'archive'), t('m36.common.permRequired', { perm: 'M36_ASSET_ARCHIVE' }))}>{t('m36.repository.archive')}</Button>
                    )}
                    {versionId === r.assetId ? (
                      <>
                        <input aria-label={`version-number-${r.assetCode}`} value={versionNumber} onChange={(e) => setVersionNumber(e.target.value)} placeholder="v2.0" />
                        <Button variant="primary" type="button" aria-label={`confirm-version-${r.assetCode}`} disabled={disabled || !versionNumber.trim()} onClick={() => void run(r, t('m36.repository.act.versionRecorded'), () => apiRequest(`/v1/dam/assets/${r.assetId}/versions`, { method: 'POST', body: { versionNumber: versionNumber.trim() }, token }), t('m36.common.permRequired', { perm: 'M36_VERSION_MANAGE' }))}>{t('m36.repository.saveVersion')}</Button>
                        <Button variant="danger" type="button" aria-label={`cancel-version-${r.assetCode}`} onClick={() => { setVersionId(null); setVersionNumber(''); }}>{t('common.cancel')}</Button>
                      </>
                    ) : (
                      <Button variant="secondary" type="button" aria-label={`version-${r.assetCode}`} onClick={() => { setVersionId(r.assetId); setVersionNumber(''); setNotice(null); }}>{t('m36.repository.newVersion')}</Button>
                    )}
                    {catId === r.assetId ? (
                      <>
                        <select aria-label={`category-${r.assetCode}`} value={catValue} onChange={(e) => setCatValue(e.target.value)}>
                          {ACTIVE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <Button variant="secondary" type="button" aria-label={`confirm-category-${r.assetCode}`} disabled={disabled} onClick={() => void run(r, t('m36.repository.act.recategorised'), () => apiRequest(`/v1/dam/assets/${r.assetId}`, { method: 'PUT', body: { category: catValue }, token }))}>{t('m36.repository.apply')}</Button>
                        <Button variant="danger" type="button" aria-label={`cancel-category-${r.assetCode}`} onClick={() => setCatId(null)}>{t('common.cancel')}</Button>
                      </>
                    ) : (
                      <Button variant="secondary" type="button" aria-label={`categorize-${r.assetCode}`} onClick={() => { setCatId(r.assetId); setCatValue(r.category); setNotice(null); }}>{t('m36.repository.categorize')}</Button>
                    )}
                    <Button variant="danger" type="button" aria-label={`delete-${r.assetCode}`} disabled={disabled} onClick={() => void run(r, t('m36.repository.act.deleted'), () => apiRequest(`/v1/dam/assets/${r.assetId}`, { method: 'DELETE', token }))}>{t('m36.repository.delete')}</Button>
                  </>
                );
              } },
            ]}
          />
        </>
      )}
    </main>
  );
}
